import { Controller } from 'stimulus';
import Swiper, {A11y, EffectFade, Pagination } from 'swiper';

Swiper.use([A11y, EffectFade, Pagination]);

export default class extends Controller {
  static targets = ['slider', 'pagination'];

  connect() {
    this.initSwiper();
  }

  disconnect() {
    this.destroySwiper();
  }

  /**
   * initSwiper
   * - Initialize the Swiper instance
   */
  initSwiper() {
    const slideLength = this.sliderTarget.querySelectorAll('.swiper-slide').length;
    if (slideLength <= 1) return;

    this.swiper = new Swiper(this.sliderTarget, {
      slidesPerView: 1,
      slidesPerGroup: 1,
      autoHeight: true,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      pagination: {
        el: this.paginationTarget,
        clickable: false,
        renderBullet: (index, className) => {
          return `
            <li class="swiper-pagination-item">
              <button class="${className}" type="button" data-index="${index}" ${index === 0 ? 'aria-current="true"' : ''}>
                <span class="sr-only">Go to slide ${index + 1}</span>
              </button>
            </li>
          `;
        },
      },
      centeredSlides: true,
      a11y: {
        liveRegion: false,
        slideLabelMessage: 'Slide {{index}} of {{slidesLength}}'
      },
      on: {
        afterInit: (swiper) => {
          setTimeout(() => {
            swiper.wrapperEl.removeAttribute('aria-live');
          }, 300);

          this.addPaginationListeners(swiper);
        },
        slideChange: () => {
          this.updateAriaNotification();
        },
      }
    });

    // Hack-fix for Swiper pagination not updating after a swipe gesture
    this.swiper.on('slideChangeTransitionEnd', () => {
      if (!this.swiper.pagination) return;

      const bullets = this.swiper.pagination.bullets;
      bullets.forEach((bullet, index) => {
        if (this.swiper.activeIndex === index) {
          bullet.setAttribute('aria-current', 'true');
        } else {
          bullet.removeAttribute('aria-current');
        }
      });

      this.setViewableSlides(this.swiper);
    });

    // Update the aria-notification element with the initial slide details
    this.updateAriaNotification();
    this.setViewableSlides(this.swiper);
  }

  addPaginationListeners(swiper) {
    const bullets = swiper.pagination.bullets;
    bullets.forEach((bullet) => this.addBulletListener(bullet, swiper));
  }

  addBulletListener(bullet, swiper) {
    bullet.addEventListener('click', () => swiper.slideTo(bullet.dataset.index));
  }

  /**
   * updateAriaNotification
   * - Manually updates the swiper-notification element with slide change details
   */
  updateAriaNotification() {
    // Find the swiper-notification element specific to this Swiper instance
    const notification = this.sliderTarget.querySelector('.swiper-notification');

    // If it exists
    if (notification) {
      // Get the current slide index and total slides
      const currentSlideIndex = this.swiper.realIndex + 1;
      const totalSlides = this.swiper.slides.length;

      // Update the innerText of the notification element
      notification.innerText = `Slide ${currentSlideIndex} of ${totalSlides} is active.`;
    }
  }

  setViewableSlides({slides}) {
    slides.forEach(slide => this.setViewableSlide(slide, slide.classList.contains('swiper-slide-visible')));
  }

  setViewableSlide(slide, isViewable) {
    slide.setAttribute('aria-hidden', !isViewable);

    const slideFocusableElements = slide.querySelectorAll('button, a');
    slideFocusableElements.forEach(elem => isViewable
      ? elem.removeAttribute('tabindex')
      : elem.setAttribute('tabindex', -1)
    );
  }

  /**
   * destroySwiper
   * - Destroy the Swiper instance, if it exists
   */
  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }
}
